// Lib
import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { connect } from 'react-redux';
import { compose } from '../../../../node_module_clones/recompose';

// Constants
import { DROP_TARGET_TYPES } from '../../../../common/elements/elementConstants';

// Actions
import { navigateToBoard } from '../../../reducers/navigationActions';

// Utils
import { getIsPublicTemplateChild, isTemplatePublicParentBoard } from '../../../../common/templates/templateUtil';
import { hasCommandModifier } from '../../../utils/keyboard/keyboardUtility';

// Drop Utils
import makeCanDropFn from '../../../element/dnd/canDrop/makeCanDropFn';
import preventLine from '../../../element/dnd/canDrop/preventLinePredicate';
import preventAnnotation from '../../../element/dnd/canDrop/preventAnnotationPredicate';
import preventSnappedDragDrop from '../../../element/dnd/canDrop/preventSnappedDragDrop';
import preventDropOnCurrentBoard from '../../../element/dnd/canDrop/preventDropOnCurrentBoardPredicate';
import validElementPermissionsPredicate from '../../../element/dnd/canDrop/validElementPermissionsPredicate';
import inboxDropLocationFn from '../../../inbox/inboxDropLocationFn';

// Components
import navigateOnHoverDecorator from '../../../element/dnd/navigateOnHoverDecorator';
import ElementDropTarget from '../../../element/dnd/elementDropTargets/ElementDropTarget';
import BreadcrumbIcon from './BreadcrumbIcon';

import createBreadcrumbSelector from './breadcrumbEntrySelector';

// Styles
import './Breadcrumb.scss';
import { buildUrl } from '../../../../common/utils/urlUtil';

const BreadcrumbTitle = ({ title }) => <div className="breadcrumb-title">{title}</div>;

BreadcrumbTitle.propTypes = {
    title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
};

const mapDispatchToProps = (dispatch) => ({
    dispatchNavigateToBoard: (boardId, keepSelection, newTab) =>
        dispatch(navigateToBoard({ boardId, keepSelection, newTab })),
});

const dropTargetConfig = {
    hoverType: DROP_TARGET_TYPES.BREADCRUMB,
    drop: inboxDropLocationFn,
    canDrop: makeCanDropFn([
        preventSnappedDragDrop,
        preventLine,
        preventAnnotation,
        preventDropOnCurrentBoard,
        validElementPermissionsPredicate,
    ]),
    collect: (monitor) => ({
        isHovered: monitor.canDrop() && monitor.isOver({ shallow: true }),
        // Requires canDrop for the breadcrumb highlighting
        canDrop: monitor.canDrop(),
    }),
};

const enhance = compose(
    connect(createBreadcrumbSelector, mapDispatchToProps),
    ElementDropTarget(dropTargetConfig),
    navigateOnHoverDecorator,
);

const Breadcrumb = (props) => {
    const {
        currentBoardId,
        elementId,
        element,
        canDrop,
        isHovered,
        isCurrentBoard,
        isRoot,
        index,
        showNavigationFeedback,
        isLast,
        isAfterTruncate,
        connectDropTarget,
        dispatchNavigateToBoard,
        closestPermissionId,
    } = props;

    const linkURL = buildUrl(`/${elementId}`, {
        p: closestPermissionId,
    });

    const handleNavigateToBoard = (event) => {
        event.preventDefault();

        if (currentBoardId === elementId) return;

        dispatchNavigateToBoard(elementId, false, hasCommandModifier(event));
    };

    const classes = classNames('Breadcrumb', {
        'can-drop': canDrop,
        hovered: isHovered,
        current: isCurrentBoard,
        root: isRoot,
        'pre-nav': showNavigationFeedback,
        'after-truncate': isAfterTruncate,
        first: index === 0,
        last: isLast,
        'template-public-parent': isTemplatePublicParentBoard(element),
        'template-public-child': getIsPublicTemplateChild(element),
    });

    const breadcrumbChildren = (
        <>
            <BreadcrumbIcon {...props} />
            <BreadcrumbTitle {...props} />
        </>
    );

    if (isCurrentBoard) {
        return (
            <div className={classes}>
                <div className="BreadcrumbContents">{breadcrumbChildren}</div>
            </div>
        );
    }

    return (
        <div ref={connectDropTarget} className={classes}>
            <a className="BreadcrumbContents" href={linkURL} onClick={handleNavigateToBoard}>
                {breadcrumbChildren}
            </a>
        </div>
    );
};

Breadcrumb.propTypes = {
    elementId: PropTypes.string,
    currentBoardId: PropTypes.string.isRequired,
    index: PropTypes.number,
    canDrop: PropTypes.bool,
    isCurrentBoard: PropTypes.bool,
    isHovered: PropTypes.bool,
    isRoot: PropTypes.bool,
    isLast: PropTypes.bool,
    isLoading: PropTypes.bool,
    closestPermissionId: PropTypes.string,
    showNavigationFeedback: PropTypes.bool,
    isAfterTruncate: PropTypes.bool,
    element: PropTypes.object,
    currentUser: PropTypes.object,
    dispatchNavigateToBoard: PropTypes.func,
    connectDropTarget: PropTypes.func,
    updateUser: PropTypes.func,
    showTooltip: PropTypes.func,
};

export default enhance(Breadcrumb);
